/**
 * Sidebar data: every component-library entry grouped by category. Descriptions
 * live with each component (in the barrel and on its docs page), so the sidebar
 * only needs the id and name to render and link.
 */

import {
  BookOpen,
  Coins,
  Eye,
  Info,
  Layers,
  LayoutPanelLeft,
  MousePointerClick,
  Pilcrow,
  Sparkles,
  SquareTerminal,
} from 'lucide-react'

import { ComponentCategory } from '~/components/component-library/internal/Sidebar/types'

const foundations: ComponentCategory = {
  name: 'Foundations',
  icon: BookOpen,
  components: [
    { id: 'guidelines', name: 'Guidelines' },
    { id: 'typography', name: 'Typography' },
    { id: 'colors', name: 'Colors' },
    { id: 'overlays', name: 'Overlays' },
    { id: 'building-components', name: 'Building components' },
    { id: 'inputs', name: 'Inputs' },
  ],
}

const displays: ComponentCategory = {
  name: 'Displays',
  icon: Eye,
  components: [
    { id: 'address-display', name: 'AddressDisplay' },
    { id: 'network-display', name: 'NetworkDisplay' },
    { id: 'token-display', name: 'TokenDisplay' },
    { id: 'user-display', name: 'UserDisplay' },
  ],
}

const balances: ComponentCategory = {
  name: 'Balances',
  icon: Coins,
  components: [
    { id: 'currency-amount', name: 'CurrencyAmount' },
    { id: 'token-balance', name: 'TokenBalance' },
    { id: 'segmented-assets-bar', name: 'SegmentedAssetsBar' },
  ],
}

const layouts: ComponentCategory = {
  name: 'Layouts',
  icon: LayoutPanelLeft,
  components: [
    { id: 'page-layout', name: 'PageLayout' },
    { id: 'list', name: 'List' },
    { id: 'table', name: 'Table' },
    { id: 'group', name: 'Group' },
    { id: 'label-value', name: 'LabelValue' },
    { id: 'tabs', name: 'Tabs' },
    { id: 'breadcrumb', name: 'Breadcrumb' },
    { id: 'collapsible', name: 'Collapsible' },
  ],
}

const inputs: ComponentCategory = {
  name: 'Inputs',
  icon: Pilcrow,
  components: [
    { id: 'text-input', name: 'TextInput' },
    { id: 'text-area-input', name: 'TextAreaInput' },
    { id: 'address-input', name: 'AddressInput' },
    { id: 'address-search-input', name: 'AddressSearchInput' },
    { id: 'checkbox-group-input', name: 'CheckboxGroupInput' },
    { id: 'combobox-input', name: 'ComboboxInput' },
    { id: 'date-input', name: 'DateInput' },
    { id: 'number-input', name: 'NumberInput' },
    { id: 'radio-group-input', name: 'RadioGroupInput' },
    { id: 'segmented-control', name: 'SegmentedControl' },
    { id: 'select-input', name: 'SelectInput' },
    { id: 'smart-account-select-input', name: 'SmartAccountSelectInput' },
    { id: 'token-selector', name: 'TokenSelector' },
  ],
}

const buttons: ComponentCategory = {
  name: 'Buttons',
  icon: MousePointerClick,
  components: [
    { id: 'button', name: 'Button' },
    { id: 'copyable', name: 'Copyable' },
    { id: 'learn-more-link', name: 'LearnMoreLink' },
    { id: 'filter-trigger', name: 'FilterTrigger' },
    { id: 'toggleable', name: 'Toggleable' },
  ],
}

const overlays: ComponentCategory = {
  name: 'Overlays',
  icon: Layers,
  components: [
    { id: 'dialog', name: 'Dialog' },
    { id: 'dropdown', name: 'Dropdown' },
    { id: 'tooltip', name: 'Tooltip' },
  ],
}

const feedback: ComponentCategory = {
  name: 'Feedback',
  icon: Info,
  components: [
    { id: 'message', name: 'Message' },
    { id: 'toast', name: 'Toast' },
    { id: 'loading-spinner', name: 'LoadingSpinner' },
    { id: 'skeleton', name: 'Skeleton' },
    { id: 'empty-state', name: 'EmptyState' },
  ],
}

const decorations: ComponentCategory = {
  name: 'Decorations',
  icon: Sparkles,
  components: [
    { id: 'pill', name: 'Pill' },
    { id: 'network-logo', name: 'NetworkLogo' },
    { id: 'checkbox', name: 'Checkbox' },
    { id: 'toggle', name: 'Toggle' },
  ],
}

const other: ComponentCategory = {
  name: 'Other',
  icon: SquareTerminal,
  components: [
    { id: 'timestamp', name: 'Timestamp' },
    { id: 'animated-number', name: 'AnimatedNumber' },
    { id: 'state-change-list', name: 'StateChangeList' },
  ],
}

export const componentCategories: ComponentCategory[] = [
  foundations,
  displays,
  balances,
  layouts,
  inputs,
  buttons,
  overlays,
  feedback,
  decorations,
  other,
]
